import { useState, useMemo, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  X,
  ChevronRight,
  ChevronLeft,
  BarChart2,
  LineChart,
  PieChart,
  AreaChart,
  Table2,
  FileSpreadsheet,
  Check,
  AlertCircle,
} from 'lucide-react';
import { useStore } from '../../store';
import { parseDataFile, isDataFile, getNumericColumns, getCategoricalColumns } from '../../utils/dataParsers';
import type { ChartType, ChartConfig, TableConfig, VisualBlock } from '../../types/visualizations';
import ChartBlock from './ChartBlock';
import TableBlock from './TableBlock';

interface DataVizCreatorProps {
  sectionId: number;
  onClose: () => void;
}

type WizardStep = 'select-data' | 'choose-type' | 'configure' | 'preview';

const CHART_TYPES: { id: ChartType; name: string; icon: typeof BarChart2; description: string }[] = [
  { id: 'bar', name: 'Bar Chart', icon: BarChart2, description: 'Compare values across categories' },
  { id: 'line', name: 'Line Chart', icon: LineChart, description: 'Show trends over time or sequence' },
  { id: 'area', name: 'Area Chart', icon: AreaChart, description: 'Show cumulative totals over time' },
  { id: 'pie', name: 'Pie Chart', icon: PieChart, description: 'Show parts of a whole' },
  { id: 'table', name: 'Data Table', icon: Table2, description: 'Display data in rows and columns' },
];

export default function DataVizCreator({ sectionId, onClose }: DataVizCreatorProps) {
  const { sections, localFiles, selectedDocuments, parsedDatasets, addParsedDataset, addVisualBlock } = useStore();

  const [step, setStep] = useState<WizardStep>('select-data');
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null);
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [parseError, setParseError] = useState<string | null>(null);

  // Chart configuration
  const [xAxis, setXAxis] = useState<string>('');
  const [yAxis, setYAxis] = useState<string[]>([]);
  const [chartTitle, setChartTitle] = useState<string>('');
  const [showLegend, setShowLegend] = useState<boolean>(true);

  // Table configuration
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [maxRows, setMaxRows] = useState<number>(10);
  const [striped, setStriped] = useState<boolean>(true);

  // Get section documents that are data files
  const section = sections.find((s) => s.id === sectionId);
  const sectionDocs = section?.documents || [];

  // Combine all available files
  const allFiles = useMemo(() => {
    const combined = [...sectionDocs, ...(localFiles || []), ...(selectedDocuments || [])];
    // Remove duplicates by id
    const uniqueMap = new Map();
    combined.forEach((f) => {
      if (!uniqueMap.has(f.id)) {
        uniqueMap.set(f.id, f);
      }
    });
    return Array.from(uniqueMap.values());
  }, [sectionDocs, localFiles, selectedDocuments]);

  // Filter to data files only
  const dataFiles = useMemo(() => {
    return allFiles.filter((f) => {
      const filename = 'filename' in f ? f.filename : ('name' in f ? (f as any).name : '');
      return isDataFile(filename);
    });
  }, [allFiles]);

  // Get current dataset
  const currentDataset = useMemo(() => {
    if (selectedDatasetId) {
      return parsedDatasets.find((d) => d.id === selectedDatasetId);
    }
    return null;
  }, [selectedDatasetId, parsedDatasets]);

  // Get numeric and categorical columns
  const numericCols = useMemo(() => {
    return currentDataset ? getNumericColumns(currentDataset) : [];
  }, [currentDataset]);

  const categoricalCols = useMemo(() => {
    return currentDataset ? getCategoricalColumns(currentDataset) : [];
  }, [currentDataset]);

  // Parse selected file
  useEffect(() => {
    if (!selectedFileId) return;

    const file = dataFiles.find((f) => f.id === selectedFileId);
    if (!file) return;

    const content = 'content' in file ? file.content : undefined;
    if (!content) {
      setParseError('File content not available. Please ensure the file has been loaded.');
      return;
    }

    const filename = 'filename' in file ? file.filename : ('name' in file ? (file as any).name : 'data');

    const dataset = parseDataFile(content, filename);
    if (!dataset) {
      setParseError('Failed to parse file. Please ensure it is a valid CSV or JSON file.');
      return;
    }

    // Add source file reference
    dataset.sourceFileId = selectedFileId;

    // Check if we already have this dataset
    const existingDataset = parsedDatasets.find((d) => d.sourceFileId === selectedFileId);
    if (existingDataset) {
      setSelectedDatasetId(existingDataset.id);
    } else {
      addParsedDataset(dataset);
      setSelectedDatasetId(dataset.id);
    }

    setParseError(null);

    // Auto-select first columns
    if (dataset.headers.length > 0) {
      setXAxis(dataset.headers[0]);
      const numCols = getNumericColumns(dataset);
      if (numCols.length > 0) {
        setYAxis([numCols[0]]);
      } else if (dataset.headers.length > 1) {
        setYAxis([dataset.headers[1]]);
      }
      setSelectedColumns(dataset.headers.slice(0, 5));
    }
  }, [selectedFileId, dataFiles, parsedDatasets, addParsedDataset]);

  // Handle Y-axis multi-select
  const toggleYAxis = (col: string) => {
    if (yAxis.includes(col)) {
      setYAxis(yAxis.filter((c) => c !== col));
    } else {
      setYAxis([...yAxis, col]);
    }
  };

  // Handle column selection for tables
  const toggleColumn = (col: string) => {
    if (selectedColumns.includes(col)) {
      setSelectedColumns(selectedColumns.filter((c) => c !== col));
    } else {
      setSelectedColumns([...selectedColumns, col]);
    }
  };

  // Build config based on type
  const buildConfig = (): ChartConfig | TableConfig | null => {
    if (!currentDataset) return null;

    if (chartType === 'table') {
      return {
        datasetId: currentDataset.id,
        columns: selectedColumns.length > 0 ? selectedColumns : undefined,
        maxRows: maxRows,
        striped: striped,
      } as TableConfig;
    }

    return {
      chartType: chartType,
      datasetId: currentDataset.id,
      xAxis: xAxis,
      yAxis: yAxis.length > 0 ? yAxis : undefined,
      labelColumn: chartType === 'pie' ? xAxis : undefined,
      valueColumn: chartType === 'pie' && yAxis.length > 0 ? yAxis[0] : undefined,
      title: chartTitle || undefined,
      showLegend: showLegend,
    } as ChartConfig;
  };

  // Handle insert
  const handleInsert = () => {
    const config = buildConfig();
    if (!config) return;

    const block: VisualBlock = chartType === 'table'
      ? {
          id: uuidv4(),
          sectionId: sectionId,
          type: 'table',
          position: 'after',
          width: 'large',
          alignment: 'center',
          config: config as TableConfig,
        }
      : {
          id: uuidv4(),
          sectionId: sectionId,
          type: 'chart',
          position: 'after',
          width: 'large',
          alignment: 'center',
          config: config as ChartConfig,
        };

    addVisualBlock(sectionId, block);
    onClose();
  };

  const canProceed = () => {
    switch (step) {
      case 'select-data':
        return selectedDatasetId !== null && !parseError;
      case 'choose-type':
        return true;
      case 'configure':
        if (chartType === 'table') {
          return selectedColumns.length > 0;
        }
        return xAxis && yAxis.length > 0;
      case 'preview':
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (step === 'select-data') setStep('choose-type');
    else if (step === 'choose-type') setStep('configure');
    else if (step === 'configure') setStep('preview');
  };

  const prevStep = () => {
    if (step === 'choose-type') setStep('select-data');
    else if (step === 'configure') setStep('choose-type');
    else if (step === 'preview') setStep('configure');
  };

  const renderStepContent = () => {
    switch (step) {
      case 'select-data':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Select a CSV or JSON file to create a visualization. Files can be attached to this section or uploaded globally.
            </p>

            {dataFiles.length === 0 ? (
              <div className="p-6 bg-gray-50 rounded-lg text-center">
                <FileSpreadsheet className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">No data files found</p>
                <p className="text-xs text-gray-400 mt-1">
                  Upload a CSV or JSON file to create visualizations
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {dataFiles.map((file) => {
                  const filename = 'filename' in file ? file.filename : ('name' in file ? (file as any).name : 'File');
                  const isSelected = selectedFileId === file.id;
                  const hasContent = 'content' in file && file.content;

                  return (
                    <button
                      key={file.id}
                      onClick={() => hasContent && setSelectedFileId(file.id)}
                      disabled={!hasContent}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                        isSelected
                          ? 'border-accent bg-accent/5'
                          : hasContent
                          ? 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          : 'border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed'
                      }`}
                    >
                      <FileSpreadsheet className={`w-5 h-5 ${isSelected ? 'text-accent' : 'text-gray-400'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-accent' : 'text-gray-700'}`}>
                          {filename}
                        </p>
                        {!hasContent && (
                          <p className="text-xs text-gray-400">Content not loaded</p>
                        )}
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-accent flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}

            {parseError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <p className="text-sm">{parseError}</p>
              </div>
            )}

            {currentDataset && (
              <div className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-green-700 font-medium">
                  {currentDataset.name}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {currentDataset.headers.length} columns, {currentDataset.rows.length} rows
                </p>
              </div>
            )}
          </div>
        );

      case 'choose-type':
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Choose the type of visualization to create.
            </p>

            <div className="grid grid-cols-2 gap-3">
              {CHART_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = chartType === type.id;

                return (
                  <button
                    key={type.id}
                    onClick={() => setChartType(type.id)}
                    className={`p-4 rounded-lg border text-left transition-colors ${
                      isSelected
                        ? 'border-accent bg-accent/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={`w-6 h-6 mb-2 ${isSelected ? 'text-accent' : 'text-gray-500'}`} />
                    <p className={`text-sm font-medium ${isSelected ? 'text-accent' : 'text-gray-700'}`}>
                      {type.name}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
        );

      case 'configure':
        if (chartType === 'table') {
          return (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Configure your data table.</p>

              {/* Column Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Columns to Display
                </label>
                <div className="flex flex-wrap gap-2 max-h-[150px] overflow-y-auto p-2 border border-gray-200 rounded-lg">
                  {currentDataset?.headers.map((col) => (
                    <button
                      key={col}
                      onClick={() => toggleColumn(col)}
                      className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                        selectedColumns.includes(col)
                          ? 'bg-accent text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {col}
                    </button>
                  ))}
                </div>
              </div>

              {/* Max Rows */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Rows
                </label>
                <input
                  type="number"
                  value={maxRows}
                  onChange={(e) => setMaxRows(parseInt(e.target.value) || 10)}
                  min={1}
                  max={100}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
                />
              </div>

              {/* Striped Rows */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={striped}
                  onChange={(e) => setStriped(e.target.checked)}
                  className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                />
                <span className="text-sm text-gray-700">Striped rows</span>
              </label>
            </div>
          );
        }

        // Chart configuration
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Configure your chart axes and settings.</p>

            {/* X-Axis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {chartType === 'pie' ? 'Labels (Categories)' : 'X-Axis (Categories)'}
              </label>
              <select
                value={xAxis}
                onChange={(e) => setXAxis(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
              >
                <option value="">Select column...</option>
                {currentDataset?.headers.map((col) => (
                  <option key={col} value={col}>
                    {col} {categoricalCols.includes(col) ? '(text)' : '(number)'}
                  </option>
                ))}
              </select>
            </div>

            {/* Y-Axis */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {chartType === 'pie' ? 'Values' : 'Y-Axis (Values)'}
                {chartType !== 'pie' && <span className="text-gray-400 font-normal"> - select one or more</span>}
              </label>
              <div className="flex flex-wrap gap-2 max-h-[120px] overflow-y-auto p-2 border border-gray-200 rounded-lg">
                {numericCols.length > 0 ? (
                  numericCols.map((col) => (
                    <button
                      key={col}
                      onClick={() => chartType === 'pie' ? setYAxis([col]) : toggleYAxis(col)}
                      className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                        yAxis.includes(col)
                          ? 'bg-accent text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {col}
                    </button>
                  ))
                ) : (
                  <p className="text-xs text-gray-400 p-2">No numeric columns found</p>
                )}
              </div>
            </div>

            {/* Chart Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chart Title (optional)
              </label>
              <input
                type="text"
                value={chartTitle}
                onChange={(e) => setChartTitle(e.target.value)}
                placeholder="Enter chart title..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-accent"
              />
            </div>

            {/* Show Legend */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showLegend}
                onChange={(e) => setShowLegend(e.target.checked)}
                className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
              />
              <span className="text-sm text-gray-700">Show legend</span>
            </label>
          </div>
        );

      case 'preview':
        const config = buildConfig();
        return (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">Preview your visualization before inserting.</p>

            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              {chartType === 'table' && config ? (
                <TableBlock config={config as TableConfig} />
              ) : config ? (
                <ChartBlock config={config as ChartConfig} />
              ) : (
                <div className="p-6 text-center text-gray-400">
                  Unable to generate preview
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const stepLabels: Record<WizardStep, string> = {
    'select-data': 'Select Data',
    'choose-type': 'Chart Type',
    'configure': 'Configure',
    'preview': 'Preview',
  };

  const steps: WizardStep[] = ['select-data', 'choose-type', 'configure', 'preview'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">Add Data Visualization</h3>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
          {steps.map((s, index) => (
            <div key={s} className="flex items-center">
              <div
                className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                  step === s
                    ? 'bg-accent text-white'
                    : steps.indexOf(step) > index
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {steps.indexOf(step) > index ? <Check className="w-3 h-3" /> : index + 1}
              </div>
              <span
                className={`ml-2 text-xs font-medium hidden sm:inline ${
                  step === s ? 'text-accent' : 'text-gray-500'
                }`}
              >
                {stepLabels[s]}
              </span>
              {index < steps.length - 1 && (
                <ChevronRight className="w-4 h-4 text-gray-300 mx-2" />
              )}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200">
          <button
            onClick={prevStep}
            disabled={step === 'select-data'}
            className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg ${
              step === 'select-data'
                ? 'text-gray-300 cursor-not-allowed'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>

          {step === 'preview' ? (
            <button
              onClick={handleInsert}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-accent text-white rounded-lg hover:bg-accent-dark"
            >
              <Check className="w-4 h-4" />
              Insert
            </button>
          ) : (
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className={`flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg ${
                canProceed()
                  ? 'bg-accent text-white hover:bg-accent-dark'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
